import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import config from '../config';
import { createAuth } from '../Helpers';
import Timer from '../Timer';

export const reducerName = 'storyState';

const defaultStoryId = 0;

const initialState = {
    group: '',
    stories: [
        {
            //name: 'Avedøre SafetyInduction',
            //cultureCode: 'da-DK',
            id: 0,
            chapters: [],
            elements: [],
            imagePath: ''
        }
    ],
    elements: [],
    showWeatherPopup: false,
    nextChapter: true,
    playing: false,
    currentStoryId: defaultStoryId, // hardcode til det danske id
    currentChapter: 0, // array-index
    currentChapterId: 0,
    currentElementActions: {},
    loading: { getStories: false, getElements: false, getEvents: false },
    infoPopups: [
        { group: 'AVV', src: '/question_AVV.png', visible: false },
        { group: 'ASV', src: '/question_ASV.png', visible: false },
        { group: 'ESV', src: '/question_ESV.png', visible: false },
        { group: 'HEV', src: '/question_HEV.png', visible: false },
        { group: 'KYV', src: '/question_KYV.png', visible: false },
        { group: 'SKV', src: '/question_SKV.png', visible: false },
        { group: 'SSV', src: '/question_SSV.png', visible: false },
    ],
    status: 'idle',
    error: null,
};

let timers = [];
let infoPopupTimers = [];
let weatherPopupTimers = [];
const clearTimers = () => { timers.forEach(t => t.pause()); timers.length = 0; };
const addTimer = (actionFunc, delay) => { timers.push(new Timer(actionFunc, delay)); };
const clearInfoPopupTimers = () => { infoPopupTimers.forEach(t => t.pause()); infoPopupTimers.length = 0; };
const clearWeatherPopupTimers = () => { weatherPopupTimers.forEach(t => t.pause()); weatherPopupTimers.length = 0; };
const addInfoPopupTimer = (actionFunc, delay) => { clearInfoPopupTimers(); infoPopupTimers.push(new Timer(actionFunc, delay)); };
const addWeatherPopupTimer = (actionFunc, delay) => { clearWeatherPopupTimers(); weatherPopupTimers.push(new Timer(actionFunc, delay)); };

export const getAllStoriesAndElements = createAsyncThunk(
    'storyState/getAllStoriesAndElements',
    async (currentStoryId, { getState, rejectWithValue, dispatch }) => {
        try {
            const group = getState().deviceState.activeDevice.group;
            const storyResponse = await axios({
                method: 'GET',
                url: `${config.server}/api/interactiveStories?group=${group}`,
                withCredentials: true,
                auth: createAuth(getState)
            });

            let storyData = storyResponse.data;
            if (storyData && storyData.length > 0) {
                storyData = storyData.map(story => ({ ...story, elements: [] }));
            }

            // hent kun elementerne, hvis der blev fundet nogen historier
            if (storyData.length > 0) {
                const elementResponse = await axios({
                    method: 'GET',
                    url: `${config.server}/api/elements`,
                    withCredentials: true,
                    auth: createAuth(getState)
                });
                return { storyData, elementData: elementResponse.data, currentStoryId };
            }
            return { storyData: [], elementData: [], currentStoryId };
        } catch (error) {
            console.log('ERROR FETCHING STORIES/ELEMENTS', error);
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

const storySlice = createSlice({
    name: reducerName,
    initialState,
    reducers: {
        setPlaying: (state, action) => { 
            state.playing = action.payload; 
        },
        setCurrentChapter: (state, action) => {
            state.nextChapter = true;
            state.currentChapter = action.payload;
            const story = state.stories.find(s => s.id === state.currentStoryId);
            if (story && story.chapters[action.payload]) {
                state.currentChapterId = story.chapters[action.payload].id;
            }
        },
        //brug kun denne når det er en 'clickAndStartChapter' handling. Ellers brug setCurrentChapter.
        setSelectedChapter: (state, action) => {
            const story = state.stories.find(s => s.id === state.currentStoryId);
            let currentChapterIndex = 0;
            if (story) {
                for (let i = 0; i < story.chapters.length; i++) {
                    if (story.chapters[i].id === action.payload) {
                        currentChapterIndex = i;
                    }
                }
                state.nextChapter = false;
                state.currentChapter = currentChapterIndex;
                state.currentChapterId = story.chapters[currentChapterIndex].id;
            }
        },
        setCurrentElementAction: (state, action) => {
            const { destinationElementId, property, value } = action.payload;
            state.currentElementActions[destinationElementId] = {
                ...state.currentElementActions[destinationElementId],
                [property]: value
            };
        },
        clearCurrentElementActions: (state) => {
            state.currentElementActions = {};
        },        
        setPropertyElementAction: (state, action) => {
            const { newAction, toggledByZoomPhotoClickAction } = action.payload;
            const indexOfSourceElement = state.elements.findIndex(element => element.id === newAction.sourceElementId);
            let originalValue = state.elements[indexOfSourceElement][newAction.property];
            const hasStartValueProperty = state.elements[indexOfSourceElement].hasOwnProperty(newAction.startProperty);
            if (hasStartValueProperty) {
                originalValue = state.elements[indexOfSourceElement][newAction.startProperty];
            }
            const hasElementActionWithSameId = state.currentElementActions[newAction.sourceElementId];
            let hasProp = undefined;
            if (hasElementActionWithSameId) {
                hasProp = state.currentElementActions[newAction.sourceElementId].hasOwnProperty(newAction.property);
            }
            let value;
            if (!hasElementActionWithSameId || !hasProp) {
                value = newAction.value;
            } else {
                value = state.currentElementActions[newAction.sourceElementId][newAction.property] === originalValue
                    ? newAction.value
                    : originalValue;
            }
            const changedElementActions = { ...state.currentElementActions };
            if (toggledByZoomPhotoClickAction) {
                const elementIds = Object.keys(changedElementActions);
                elementIds.forEach(key => {
                    const elementAction = changedElementActions[key];
                    if (elementAction.toggledByZoomPhotoClickAction === true && elementAction.hasOwnProperty('zIndex')) {
                        const originalZIndex = state.elements[indexOfSourceElement]['zIndex'];
                        changedElementActions[key] = { ...elementAction, zIndex: originalZIndex, toggledByZoomPhotoClickAction: false };
                    }
                });
            }
            if (!hasStartValueProperty) {
                const newElements = state.elements.map(element => {
                    if (element.id === newAction.sourceElementId) {
                        return {
                            ...element,
                            [newAction.startProperty]: originalValue
                        };
                    }
                    return element;
                });
                state.currentElementActions = {
                    ...changedElementActions,
                    [newAction.sourceElementId]: {
                        [newAction.property]: value,
                        toggledByZoomPhotoClickAction: toggledByZoomPhotoClickAction
                    }
                };
                state.elements = newElements;
                return;
            }
            state.currentElementActions = {
                ...changedElementActions,
                [newAction.sourceElementId]: {
                    [newAction.property]: value,
                    toggledByZoomPhotoClickAction: toggledByZoomPhotoClickAction
                }
            };
        },
        toggleElementAction: (state, action) => {
            // Legacy logic for toggling element action, including zoom photo click handling
            const { destinationElementId, property, value, toggledByZoomPhotoClickAction } = action.payload;
            let newValue;
            if (!state.currentElementActions[destinationElementId]) {
                newValue = value;
            } else {
                newValue = !state.currentElementActions[destinationElementId][property];
            }

            // Handle zoom photo click action logic
            if (toggledByZoomPhotoClickAction && (newValue === 'true' || newValue === true)) {
                const changedElementActions = { ...state.currentElementActions };
                Object.keys(changedElementActions).forEach(key => {
                    const elementAction = changedElementActions[key];
                    if (elementAction.toggledByZoomPhotoClickAction === true && (elementAction.visible === 'true' || elementAction.visible === true)) {
                        changedElementActions[key] = { ...elementAction, visible: false, toggledByZoomPhotoClickAction: false };
                    }
                });
                state.currentElementActions = {
                    ...changedElementActions,
                    [destinationElementId]: {
                        [property]: newValue,
                        toggledByZoomPhotoClickAction: true
                    }
                };
                return;
            }
            state.currentElementActions[destinationElementId] = {
                [property]: newValue
            };
        },
        changeStoryAction: (state, action) => {
            state.playing = false;
            state.currentStoryId = action.payload.storyId;
            state.currentChapter = 0;
            state.currentElementActions = {};
        },
        toggleInfoPopup: (state, action) => {
            const { group, visible } = action.payload;
            state.infoPopups = state.infoPopups.map(i => i.group === group ? { ...i, visible: visible !== undefined ? visible : !i.visible } : { ...i });
        },
        toggleWeatherPopup: (state, action) => {
            state.showWeatherPopup = action.payload;
        }, 
    },
    extraReducers: (builder) => {
        builder
            //getAllStoriesAndElements
            .addCase(getAllStoriesAndElements.pending, (state) => {
                state.status = 'loading';
                state.error = null;
                state.loading.getStories = true;
            })
            .addCase(getAllStoriesAndElements.fulfilled, (state, action) => {
                const { storyData, elementData, currentStoryId } = action.payload;
                
                // Map elements to their respective stories
                if (storyData.length === 0) {
                    state.stories = [{
                        id: 0,
                        chapters: [],
                        elements: []
                    }];
                    state.loading.getStories = false;
                    state.loading.getElements = false;
                    state.status = 'succeeded';
                    return;
                }

                // Attach elements to stories
                const storiesWithElements = storyData.map(story => {
                    const elementsConnectedToStory = elementData.filter(e => e.interactiveStoryIDs.length > 0 && e.interactiveStoryIDs.includes(story.id));
                    return { ...story, elements: elementsConnectedToStory };
                });

                // Set alwaysVisible property
                const storiesWithElementsWithAlwaysVisibleProperty = storiesWithElements.map(story => {
                    const storyElements = story.elements.map(element => {
                        let isAlwaysVisible = false;
                        story.actions.forEach(action => {
                            if (action.actionType === 'storyAction' && action.destinationElementId === element.id) {
                                isAlwaysVisible = true;
                            }
                        });
                        return isAlwaysVisible ? { ...element, visible: isAlwaysVisible } : { ...element };
                    });
                    return { ...story, elements: storyElements };
                });

                // Set currentStoryId
                let newCurrentStoryId = currentStoryId ? currentStoryId : defaultStoryId;
                const storyWithCurrentId = storiesWithElementsWithAlwaysVisibleProperty.filter(story => story.id === newCurrentStoryId);
                const storyExists = storyWithCurrentId.length > 0;
                if (newCurrentStoryId === 0 || !storyExists) {
                    // en "kopi"-historie må aldrig være standard
                    const isCopy = s => (s.name || '').includes('kopi');
                    const defaultCandidates = storiesWithElementsWithAlwaysVisibleProperty.filter(s => s.category === 'Sikkerhedsintroduktion' && !isCopy(s));
                    const fallbackCandidates = storiesWithElementsWithAlwaysVisibleProperty.filter(s => !isCopy(s));
                    newCurrentStoryId = (defaultCandidates[0] || fallbackCandidates[0] || storiesWithElementsWithAlwaysVisibleProperty[0]).id;
                }

                state.currentStoryId = newCurrentStoryId;
                state.stories = storiesWithElementsWithAlwaysVisibleProperty;
                state.elements = elementData;
                state.loading.getStories = false;
                state.loading.getElements = false;
                state.status = 'succeeded';
            })
            .addCase(getAllStoriesAndElements.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload || 'An error occurred';
                state.loading.getStories = false;
                state.loading.getElements = false;
            });
    },
});

export const { setPlaying, setCurrentChapter, setSelectedChapter, setCurrentElementAction, clearCurrentElementActions, setPropertyElementAction, toggleElementAction, changeStoryAction, toggleInfoPopup, toggleWeatherPopup } = storySlice.actions;

export const initNewChapterThunk = (value) => (dispatch, getState) => {
    const storyState = getState().storyState;

    clearTimers();
    clearInfoPopupTimers();
    clearWeatherPopupTimers();
    
    const currentStory = storyState.stories.find(story => story.id === storyState.currentStoryId);
    if (!currentStory || !currentStory.actions) return;
    currentStory.actions
        .filter(a => a.chapterId && a.chapterId === storyState.currentChapterId)
        .forEach(action => {
            addTimer(() => {
                const valueInLowerCaseString = String(action.value).toLowerCase();
                let newActionValue = action.value;
                if (valueInLowerCaseString === "true" || valueInLowerCaseString === "false") {
                    newActionValue = valueInLowerCaseString === "true";
                }
                dispatch(setCurrentElementAction({
                    destinationElementId: action.destinationElementId,
                    property: action.property,
                    value: newActionValue
                }));
            }, action.start * 1000);
        });
};
export const clearChapterActionsThunk = () => (dispatch) => {
    clearTimers();
    clearInfoPopupTimers();
    clearWeatherPopupTimers();
    dispatch(clearCurrentElementActions());
};
export const toggleZoomPhotoActionThunk = (elementAction) => (dispatch) => {
    dispatch(toggleElementAction({
        destinationElementId: elementAction.destinationElementId,
        property: elementAction.property,
        value: elementAction.value,
        toggledByZoomPhotoClickAction: true
    }));
    const newAction = {
        sourceElementId: elementAction.sourceElementId,
        property: 'zIndex',
        startProperty: 'startZIndex',
        value: 50,
    };
    dispatch(setPropertyElementAction({
        newAction,
        toggledByZoomPhotoClickAction: true
    }));
    
};
export const toggleInfoPopupThunk = (group, visible) => (dispatch, getState) => {
    clearInfoPopupTimers();

    const groupToToggle = group || getState().deviceState.activeDevice.group;
    const infoPopupToToggle = getState().storyState.infoPopups.filter(i => i.group === groupToToggle);

    if (
        visible === undefined ||
        visible === true ||
        (infoPopupToToggle.length > 0 ? !infoPopupToToggle[0].visible : false)
    ) {
        addInfoPopupTimer(() => {
            dispatch(toggleInfoPopup({ group: groupToToggle, visible: false }));
        }, 15 * 1000);
    }

    dispatch(toggleInfoPopup({ group: groupToToggle, visible }));
};
export const toggleWeatherThunk = (visible) => (dispatch, getState) => {
    clearWeatherPopupTimers();
    if (visible !== undefined && visible === true || visible === undefined && getState().storyState.showWeatherPopup === false) {
        dispatch(toggleWeatherPopup(true));
        addWeatherPopupTimer(() => {
            dispatch(toggleWeatherPopup(false));
        }, 30 * 1000);
    } else {
        dispatch(toggleWeatherPopup(false));
    }
};
export const pauseTimersThunk = () => () => {
    timers.forEach(t => { t.pause(); });
};

export const resumeTimersThunk = () => () => {
    timers.forEach(t => { t.resume(); });
};

export default storySlice.reducer;

